/**
 * <cw-options>: the options drawer, the pack x grind picker (design-handoff/components/
 * options-drawer.md; Shopping v13's drawer2(), ported). A bottom sheet on phones, a 420 panel
 * with the jar shelf at 600+ (assets/cw-sheet.js).
 *
 * Opens on 'cw:options:open' {productId, family, source} from ADD on a product with choices,
 * either stepper button of a card holding two or more variants, and family cards.
 *
 * The data is the card's (snippets/cw-product-data.liquid): raw Shopify options mapped onto the
 * design's axes exactly as the card maps them. "Size" is the pack, sorted smallest first;
 * "Type" is a grind ("Fine"), a form ("Whole", "Whole (Raw)", "Powder") or both
 * ("Flour – Fine").
 *
 * Rules (v13 drawer2() and the sheet):
 * - Selection: the first pack, the first form, grind Fine if offered, else the first. It is
 *   kept per product for the visit, like v13's sel.
 * - A product with grinds and exactly Whole + Flour forms shows one grind row with "Whole" as
 *   its last chip; otherwise forms get their own FORM row and grinds show for Flour only.
 *   A single grind is not a choice: no row.
 * - Pack rows are the packs of the current form / grind. Each shows what the basket holds of
 *   that exact variant; ADD adds silently (the drawer stays, no toast).
 * - "Save ₹X" against the first pack's unit price when it is ₹5 or more; the row tag is the
 *   product's own pack tag, or "Best value" on the non-first pack with the biggest saving
 *   (₹5 or more, not sold out); the per-kg rate for Wheat Atta and Multigrain only.
 * - Sold out: the row fades and says "SOLD OUT" in place of ADD (the owner's call, 1 Oct: no
 *   "Back next week" promise; nothing knows when stock returns).
 * - Family chips (Flow's product family; or Wood / Cold pressed for oils, by title) switch
 *   the drawer to that member. Their badges count what the basket holds of each.
 * - The process ribbon: the product's own (custom.process_ribbon), else the sheet's rule.
 */
import { cart, MAX_PER_LINE } from '@cw/cart';
import { CwSheet } from '@cw/sheet';
import { RATE_CHIPS, hasChip, esc, rupees, packLabel, baseOf, rateOf, model } from '@cw/products';
import { setSel, rowsFor, ribbonOf, familyOf, groupsHtml, paintGroups, chipAction } from '@cw/picker';

class CwOptions extends CwSheet {
  #product = null;
  #family = null;
  #icons = {};

  connectedCallback() {
    super.connectedCallback();
    this.ribbon = this.querySelector('.cw-opt__ribbon');
    this.ribbonText = this.querySelector('[data-cw="ribbon"]');
    const icons = this.querySelector('template[data-cw="icons"]');
    for (const el of icons?.content.querySelectorAll('[data-icon]') || []) this.#icons[el.dataset.icon] = el.innerHTML.trim();
    document.addEventListener('cw:options:open', this.#onOpen);
    document.addEventListener('cw:cart:change', this.#onCart);
    this.body.addEventListener('click', this.#onBodyClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cw:options:open', this.#onOpen);
    document.removeEventListener('cw:cart:change', this.#onCart);
  }

  #onOpen = async (event) => {
    const { productId, source } = event.detail || {};
    if (!productId) return;
    event.preventDefault();
    const product = await this.#load(productId, source);
    if (!product) {
      // No data (should not happen): the product page is where choices are made.
      const link = source?.closest?.('.cw-card')?.querySelector('.cw-card__name');
      if (link) window.location.href = link.href;
      return;
    }
    // Opened in Atta & Flours, a product sold whole and as flour (a millet under Millet Flours,
    // chana under Besan) starts on its flour.
    const from = source?.closest?.('cw-aisle')?.dataset.aisle;
    if (from === 'atta' && product.forms.includes('Flour')) setSel(product, { form: 'Flour' });
    this.#show(product);
    // Focus returns to the button that was pressed (or the control's first button).
    const pressed = source?.contains?.(document.activeElement) ? document.activeElement : source?.querySelector?.('button');
    this.open(pressed || source);
  };

  /** The product's data from the page, else from /products/<handle>?view=cw-data. */
  async #load(productId, source) {
    const local = this.#find(productId);
    if (local) return local;
    const url = source?.closest?.('.cw-card')?.querySelector('.cw-card__name')?.getAttribute('href');
    if (!url) return null;
    try {
      const response = await fetch(`${url.split('?')[0]}?view=cw-data`);
      if (!response.ok) return null;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const store = document.createElement('div');
      store.hidden = true;
      store.append(...doc.querySelectorAll('script[type="application/json"]'));
      this.append(store);
      return this.#find(productId);
    } catch {
      return null;
    }
  }

  #find(productId) {
    const script = document.querySelector(`script[data-cw-product="${productId}"]`);
    if (!script) return null;
    try {
      return model(JSON.parse(script.textContent));
    } catch {
      return null;
    }
  }

  #setSel(patch) {
    setSel(this.#product, patch);
    this.#paintChoices();
  }

  #show(m) {
    this.#product = m;
    this.#family = familyOf(m);
    const ribbon = ribbonOf(m);
    this.ribbon.hidden = !ribbon;
    this.ribbonText.textContent = ribbon;
    this.dialog.setAttribute('aria-label', this.#family?.title || m.title);

    // Spices show their points as small tags until the owner rewrites them (card chip set,
    // 22 Oct); a rewritten spice gets the three tiles like the flours.
    const spices = m.aisle === 'spices' && !m.cardChip;
    const claims = Array.isArray(m.claims) ? m.claims.filter((c) => c && (c.title || c.sub)) : [];
    const title = this.#family && this.#family.head === 'Pressing' ? this.#family.title : m.title;
    const head = spices
      ? `<div class="cw-opt__head cw-opt__head--chips">
          <div class="cw-opt__thumb" data-cw="thumb"></div>
          <div class="cw-opt__headcol">
            <h2 class="cw-opt__name cw-opt-clamp2">${esc(title)}</h2>
            ${claims.length ? `<div class="cw-opt__cues">${claims.map((c) => `<span class="cw-opt__cue">${esc(c.title)}</span>`).join('')}</div>` : ''}
          </div>
        </div>`
      : `<div class="cw-opt__head">
          <div class="cw-opt__thumb" data-cw="thumb"></div>
          <div class="cw-opt__headcol">
            <h2 class="cw-opt__name cw-opt-clamp2">${esc(title)}</h2>
            ${
              claims.length
                ? `<div class="cw-opt__tiles">${claims
                    .slice(0, 3)
                    .map((c) => `<div class="cw-opt__tile"><div class="cw-opt__tile-a cw-opt-clamp2">${esc(c.title)}</div><div class="cw-opt__tile-b cw-opt-clamp2">${esc(c.sub)}</div></div>`)
                    .join('')}</div>`
                : ''
            }
          </div>
        </div>`;

    this.body.innerHTML = `${head}${groupsHtml(m, this.#family, this.#icons)}
      <div class="cw-opt__group cw-opt__group--packs">
        <div class="cw-opt__heading">Pack size</div>
        <div class="cw-opt__rows" data-cw="rows"></div>
      </div>`;
    this.body.scrollTop = 0;
    this.#paintChoices();
  }

  /** Chips, notes, thumb and rows for the current selection and basket. */
  #paintChoices() {
    const m = this.#product;
    if (!m) return;
    const sel = paintGroups(this.body, m, this.#family);

    // Rows: the packs of this form / grind
    const rows = rowsFor(m, sel);
    const thumb = this.body.querySelector('[data-cw="thumb"]');
    const shown = rows.find((v) => v.pack === sel.pack) || rows[0];
    const image = shown?.image || m.image;
    if (thumb) thumb.style.backgroundImage = image ? `url("${image}")` : '';

    const first = rows[0];
    const b0 = first ? baseOf(first.label) : null;
    let tagIdx = -1;
    let tagLabel = '';
    const own = m.packTag && m.packTag.label ? m.packTag : null;
    if (own) {
      tagIdx = rows.findIndex((v) => String(v.id) === String(own.variant) || v.label === packLabel(own.variant) || v.pack === own.variant);
      tagLabel = own.label;
    } else if (rows.length > 1 && b0) {
      let best = 0;
      rows.forEach((v, i) => {
        const b = baseOf(v.label);
        if (i > 0 && b && v.available) {
          const saving = (first.price / b0) * b - v.price;
          if (saving > best) {
            best = saving;
            tagIdx = i;
          }
        }
      });
      if (best >= 500) tagLabel = 'Best value';
      else tagIdx = -1;
    }

    const list = this.body.querySelector('[data-cw="rows"]');
    const name = this.#family?.head === 'Pressing' ? this.#family.title : m.title;
    // Rows are redrawn on every change: keep keyboard focus on the same control.
    const active = /** @type {HTMLElement|null} */ (list.contains(document.activeElement) ? document.activeElement : null);
    const focusVariant = active?.dataset.variant;
    const focusStep = active?.dataset.step;
    list.innerHTML = rows
      .map((v, i) => {
        const oos = !v.available;
        const q = cart.qtyOf(v.id);
        const b = baseOf(v.label);
        const save = !oos && i > 0 && b && b0 ? Math.round(((first.price / b0) * b - v.price) / 100) : 0;
        const rate = !oos && hasChip(m, RATE_CHIPS) ? rateOf(v.label, v.price) : '';
        const tagged = i === tagIdx && !oos;
        const what = `${name} ${v.label}`;
        let control;
        if (oos) control = '<span class="cw-opt__soldout">SOLD OUT</span>';
        else if (q > 0)
          control = `<div class="cw-opt__step">
              <button type="button" class="cw-opt__pm" data-variant="${v.id}" data-step="-1" aria-label="Remove one ${esc(what)}">${this.#icons.minus || '−'}</button>
              <span class="cw-opt__qty" aria-live="polite">${q}</span>
              <button type="button" class="cw-opt__pm" data-variant="${v.id}" data-step="1" aria-label="Add one ${esc(what)}"${q >= MAX_PER_LINE ? ' disabled' : ''}>${this.#icons.plus || '+'}</button>
            </div>`;
        else control = `<button type="button" class="cw-opt__add" data-variant="${v.id}" data-step="1" aria-label="Add ${esc(what)}">ADD</button>`;
        return `<div class="cw-opt__row${tagged ? ' is-tagged' : ''}${q > 0 && !oos ? ' is-in' : ''}${oos ? ' is-oos' : ''}">
            ${tagged ? `<div class="cw-opt__tag"><span class="cw-opt__tag-fold"></span><span class="cw-opt__tag-body">${esc(tagLabel)}</span></div>` : ''}
            <div class="cw-opt__rowtext">
              <div class="cw-opt__rowline">
                <span class="cw-opt__size">${esc(v.label)}</span>
                ${save >= 5 ? `<span class="cw-opt__save">Save ₹${save.toLocaleString('en-IN')}</span>` : ''}
              </div>
              ${rate ? `<div class="cw-opt__rate cw-opt-clamp1">${esc(rate)}</div>` : ''}
            </div>
            <div class="cw-opt__prices">
              <div class="cw-opt__price">${rupees(v.price)}</div>
              ${!oos && v.compare > v.price ? `<div class="cw-opt__mrp"><span class="cw-sr">MRP </span>${rupees(v.compare)}</div>` : ''}
            </div>
            ${control}
          </div>`;
      })
      .join('');
    if (focusVariant) {
      const again =
        list.querySelector(`[data-variant="${focusVariant}"][data-step="${focusStep}"]:not(:disabled)`) ||
        list.querySelector(`[data-variant="${focusVariant}"]:not(:disabled)`);
      again?.focus({ preventScroll: true });
    }
  }

  #onBodyClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const m = this.#product;
    if (!m) return;

    const chip = chipAction(target, m);
    if (chip?.member) {
      const next = this.#find(chip.member);
      if (next) this.#show(next);
      return;
    }
    if (chip?.sel) return this.#setSel(chip.sel);

    const step = target.closest('[data-step]');
    if (step) {
      if (navigator.onLine === false) {
        document.dispatchEvent(new CustomEvent('cw:offline'));
        return;
      }
      const id = step.dataset.variant;
      const qty = cart.qtyOf(id) + Number(step.dataset.step);
      if (qty > MAX_PER_LINE) return;
      const v = m.variants.find((x) => String(x.id) === id);
      if (v) this.#setSel({ pack: v.pack });
      cart.setQty(id, Math.max(0, qty), this, { productId: m.id });
    }
  };

  #onCart = () => {
    if (this.isOpen) this.#paintChoices();
  };
}

if (!customElements.get('cw-options')) customElements.define('cw-options', CwOptions);
